export interface CommandEntry {
  name: string;
  description: string | null;
  category: string;
  type: 'hybrid' | 'slash' | 'context_menu';
  subcommandCount?: number;
}

/**
 * Real command data extracted directly from the Dem v18 bot source
 * (top-level commands and command-groups only; full subcommand-level
 * detail ships once the public Commands page reads live from the Bridge
 * per docs/IMPLEMENTATION_PLAN.md Feature 25 — see the note on that page).
 * This is a static snapshot, not fabricated sample data: every entry
 * traces to a real command in docs/V18_FEATURE_INVENTORY.md.
 */
export const commands: CommandEntry[] = [
  {
    "name": "analytics",
    "description": "Server analytics: members, activity, channels, voice, moderation, and engagement.",
    "category": "Analytics",
    "type": "hybrid",
    "subcommandCount": 10
  },
  {
    "name": "automod_dashboard",
    "description": "Open the interactive AutoMod protections dashboard.",
    "category": "AutoMod",
    "type": "hybrid"
  },
  {
    "name": "automod_disable",
    "description": "Disable a specific AutoMod protection by name.",
    "category": "AutoMod",
    "type": "hybrid"
  },
  {
    "name": "automod_enable",
    "description": "Enable a specific AutoMod protection by name.",
    "category": "AutoMod",
    "type": "hybrid"
  },
  {
    "name": "automod_status",
    "description": "View detailed status, limits, and punishments for every AutoMod feature.",
    "category": "AutoMod",
    "type": "hybrid"
  },
  {
    "name": "autoresponder",
    "description": "Create and manage custom auto-reply triggers.",
    "category": "Automation",
    "type": "hybrid",
    "subcommandCount": 21
  },
  {
    "name": "remind",
    "description": "Create and manage reminders.",
    "category": "Automation",
    "type": "hybrid",
    "subcommandCount": 3
  },
  {
    "name": "reminders_dashboard",
    "description": "Open the interactive Reminders dashboard.",
    "category": "Automation",
    "type": "hybrid"
  },
  {
    "name": "backup",
    "description": "Backup or restore this server (configuration + structure).",
    "category": "Backups",
    "type": "hybrid",
    "subcommandCount": 5
  },
  {
    "name": "backup_dashboard",
    "description": "Open the interactive Backup/Restore dashboard.",
    "category": "Backups",
    "type": "hybrid"
  },
  {
    "name": "br-create",
    "description": "Create your exclusive booster role.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "br-edit",
    "description": "Update your booster role's appearance.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "giveaway_dashboard",
    "description": "Open the interactive Giveaway system dashboard.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "glist",
    "description": "\ud83d\udcdc List all active giveaways in this server.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "gstart",
    "description": "\ud83c\udf8a Start an advanced giveaway.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "leaderboard",
    "description": "View the XP leaderboard.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "levelannounce",
    "description": "Turn level-up announcements on or off.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "levelblacklist",
    "description": "Blacklist a channel from XP.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "leveling_dashboard",
    "description": "Open the interactive Leveling system dashboard.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "levelmessage",
    "description": "Customize the level-up message. Use {user}, {level}, {server}. Pass 'none' to reset.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "levelrole",
    "description": "Manage per-level role rewards.",
    "category": "Community",
    "type": "hybrid",
    "subcommandCount": 4
  },
  {
    "name": "panel",
    "description": "Post a quick-action staff control panel in this channel.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "rank",
    "description": "View your rank profile.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "reactionrole",
    "description": "Reaction role system.",
    "category": "Community",
    "type": "hybrid",
    "subcommandCount": 4
  },
  {
    "name": "reactionrole_dashboard",
    "description": "Open the interactive Reaction Roles dashboard.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "rep",
    "description": "Give reputation to someone (once every 24h).",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "setbio",
    "description": "Set your profile bio.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "setlevelchannel",
    "description": "Set (or clear) the channel level-up messages are sent to.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "starboard",
    "description": "View or configure this server's starboard.",
    "category": "Community",
    "type": "hybrid",
    "subcommandCount": 6
  },
  {
    "name": "starboard_dashboard",
    "description": "Open the interactive Starboard dashboard.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "suggest",
    "description": "Submit a suggestion for the server.",
    "category": "Community",
    "type": "slash"
  },
  {
    "name": "suggest_setup",
    "description": "Set the channel suggestions get posted to.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "suggestions_dashboard",
    "description": "Open the interactive Suggestions system dashboard.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "voicexp",
    "description": "Enable or disable XP gain from voice channels.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "weeklylb",
    "description": "Weekly XP leaderboard.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "welcome",
    "description": "\ud83c\udfe0 Welcome system settings.",
    "category": "Community",
    "type": "hybrid",
    "subcommandCount": 5
  },
  {
    "name": "welcome_dashboard",
    "description": "Open the interactive Welcome system dashboard.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "xpboost",
    "description": "Add XP boost to a role.",
    "category": "Community",
    "type": "hybrid"
  },
  {
    "name": "Dem AI: Explain",
    "description": "Right-click a message to have Dem AI explain it.",
    "category": "Dem AI",
    "type": "context_menu"
  },
  {
    "name": "Dem AI: Summarize",
    "description": "Right-click a message to have Dem AI summarize it.",
    "category": "Dem AI",
    "type": "context_menu"
  },
  {
    "name": "ai",
    "description": "Dem AI - your AI assistant.",
    "category": "Dem AI",
    "type": "hybrid",
    "subcommandCount": 21
  },
  {
    "name": "help",
    "description": "Browse every command, organized by category, with search and pagination.",
    "category": "Help",
    "type": "hybrid"
  },
  {
    "name": "log_disable",
    "description": "Disable a specific event log type (e.g. ban, kick, raid).",
    "category": "Logging",
    "type": "hybrid"
  },
  {
    "name": "log_enable",
    "description": "Enable a specific event log type (e.g. ban, kick, raid).",
    "category": "Logging",
    "type": "hybrid"
  },
  {
    "name": "log_settings",
    "description": "View logging configuration.",
    "category": "Logging",
    "type": "hybrid"
  },
  {
    "name": "logs_dashboard",
    "description": "Open the interactive Logging configuration dashboard.",
    "category": "Logging",
    "type": "hybrid"
  },
  {
    "name": "resetlogs",
    "description": "Reset and rebuild log channels.",
    "category": "Logging",
    "type": "hybrid"
  },
  {
    "name": "set_botlog",
    "description": "Set bot/system log channel.",
    "category": "Logging",
    "type": "hybrid"
  },
  {
    "name": "set_modlog",
    "description": "Set moderation log channel.",
    "category": "Logging",
    "type": "hybrid"
  },
  {
    "name": "setuplogs",
    "description": "Setup Dem logging system.",
    "category": "Logging",
    "type": "hybrid"
  },
  {
    "name": "ban",
    "description": "Permanently ban a member from the server.",
    "category": "Moderation",
    "type": "hybrid"
  },
  {
    "name": "clear",
    "description": "Bulk delete recent messages from this channel.",
    "category": "Moderation",
    "type": "hybrid"
  },
  {
    "name": "history",
    "description": "\ud83d\udd0e View a member's moderation history.",
    "category": "Moderation",
    "type": "hybrid"
  },
  {
    "name": "kick",
    "description": "Remove a member from the server. They can rejoin with a new invite.",
    "category": "Moderation",
    "type": "hybrid"
  },
  {
    "name": "modstats",
    "description": "\ud83d\udcca View server moderation statistics.",
    "category": "Moderation",
    "type": "hybrid"
  },
  {
    "name": "nickname",
    "description": "Change a member's server nickname.",
    "category": "Moderation",
    "type": "hybrid"
  },
  {
    "name": "removewarn",
    "description": "Delete a specific warning by its ID.",
    "category": "Moderation",
    "type": "hybrid"
  },
  {
    "name": "softban",
    "description": "Ban and immediately unban a member to quickly purge their recent messages.",
    "category": "Moderation",
    "type": "hybrid"
  },
  {
    "name": "tempban",
    "description": "Ban a member for a set number of minutes, then automatically unban them.",
    "category": "Moderation",
    "type": "hybrid"
  },
  {
    "name": "timeout",
    "description": "Temporarily prevent a member from sending messages, joining voice, or reacting.",
    "category": "Moderation",
    "type": "hybrid"
  },
  {
    "name": "untimeout",
    "description": "Remove an active timeout from a member early.",
    "category": "Moderation",
    "type": "hybrid"
  },
  {
    "name": "warn",
    "description": "Add a formal warning strike to a member's record.",
    "category": "Moderation",
    "type": "hybrid"
  },
  {
    "name": "warnings",
    "description": "View a member's warning history.",
    "category": "Moderation",
    "type": "hybrid"
  },
  {
    "name": "announcement",
    "description": null,
    "category": "Owner",
    "type": "hybrid",
    "subcommandCount": 10
  },
  {
    "name": "broadcast",
    "description": "Control whether this server receives global announcements.",
    "category": "Owner",
    "type": "hybrid",
    "subcommandCount": 3
  },
  {
    "name": "antinuke",
    "description": "Manage Dem Anti-Nuke.",
    "category": "Security",
    "type": "hybrid",
    "subcommandCount": 3
  },
  {
    "name": "antinuke_dashboard",
    "description": "Open the interactive Anti-Nuke protection dashboard.",
    "category": "Security",
    "type": "hybrid"
  },
  {
    "name": "security",
    "description": "Manage Dem Security AI.",
    "category": "Security",
    "type": "hybrid",
    "subcommandCount": 4
  },
  {
    "name": "security_dashboard",
    "description": "Open the interactive Security protections dashboard.",
    "category": "Security",
    "type": "hybrid"
  },
  {
    "name": "Translate Message",
    "description": "Right-click a message to translate it to your preferred language.",
    "category": "Settings",
    "type": "context_menu"
  },
  {
    "name": "prefix",
    "description": "View or manage this server's custom command prefix.",
    "category": "Settings",
    "type": "hybrid",
    "subcommandCount": 2
  },
  {
    "name": "translate",
    "description": "Translate text, a replied-to message, or a message link into another language.",
    "category": "Settings",
    "type": "hybrid"
  },
  {
    "name": "translate_dashboard",
    "description": "Open the interactive Translation dashboard.",
    "category": "Settings",
    "type": "hybrid"
  },
  {
    "name": "translate_setlang",
    "description": "Set your preferred language for the right-click translate menu.",
    "category": "Settings",
    "type": "hybrid"
  },
  {
    "name": "ticket",
    "description": "Ticket system commands.",
    "category": "Tickets",
    "type": "hybrid",
    "subcommandCount": 7
  },
  {
    "name": "ticket_dashboard",
    "description": "Open the interactive Ticket system dashboard.",
    "category": "Tickets",
    "type": "hybrid"
  },
  {
    "name": "Steal Emojis",
    "description": "Right-click a message to copy its custom emojis into this server.",
    "category": "Utility",
    "type": "context_menu"
  },
  {
    "name": "Steal Sticker",
    "description": "Right-click a message to copy its sticker into this server.",
    "category": "Utility",
    "type": "context_menu"
  },
  {
    "name": "afk",
    "description": "Set your AFK status.",
    "category": "Utility",
    "type": "hybrid"
  },
  {
    "name": "avatar",
    "description": "View a user's full-size avatar.",
    "category": "Utility",
    "type": "hybrid"
  },
  {
    "name": "botinfo",
    "description": "View statistics about the bot itself (RAM, uptime, versions).",
    "category": "Utility",
    "type": "hybrid"
  },
  {
    "name": "emoji",
    "description": "Copy custom emojis from other servers into this one.",
    "category": "Utility",
    "type": "hybrid",
    "subcommandCount": 5
  },
  {
    "name": "ping",
    "description": "Check the bot's connection speed and response latency.",
    "category": "Utility",
    "type": "hybrid"
  },
  {
    "name": "serverinfo",
    "description": "View detailed statistics about this server.",
    "category": "Utility",
    "type": "hybrid"
  },
  {
    "name": "steal",
    "description": "Copy a custom emoji into this server - paste one, link a message, or just reply.",
    "category": "Utility",
    "type": "hybrid"
  },
  {
    "name": "stealsticker",
    "description": "Copy a sticker into this server - link a message, or just reply.",
    "category": "Utility",
    "type": "hybrid"
  },
  {
    "name": "sticker",
    "description": "Copy custom stickers from other servers into this one.",
    "category": "Utility",
    "type": "hybrid",
    "subcommandCount": 3
  },
  {
    "name": "userinfo",
    "description": "View detailed information about a user, including roles and account age.",
    "category": "Utility",
    "type": "hybrid"
  }
];